/**
 * Catalog hooks (P30 / PENDING_TODO A5.1 — closed 2026-05-07).
 *
 * TanStack Query wrappers over the control-plane catalog endpoints. All
 * routes now consume live data from these hooks; the `@sumcp/test-utils`
 * fixtures stay in `packages/test-utils/` for unit tests only.
 */
import { useQuery } from '@tanstack/react-query';
import type { McpTool } from '@sumcp/shared-types';

import { apiFetch } from './client.js';

/**
 * Server row shape as returned by `/api/v1/servers` and `/api/v1/catalog/servers`.
 *
 * NOTE: this is the Drizzle column shape (camelCase), not the canonical
 * `McpServer` entity from `@sumcp/shared-types` (which is snake_case). The
 * control-plane registry routes return rows directly from `serverRepo.list()`
 * for now; if/when we add a serializer that snake_cases the keys, this type
 * collapses back to `McpServer`.
 */
export interface McpServerRow {
  id: string;
  slug: string;
  name: string;
  description: string;
  owningWgId: string;
  supportWgId: string;
  costAttributionWgId: string;
  backendType: 'native_mcp' | 'rest_wrapper' | 'stdio';
  backendUrl: string;
  environment: string;
  status: string;
  riskTier: 'low' | 'medium' | 'high' | 'critical';
  createdAt: string;
  updatedAt: string;
}

interface ServerListResponse {
  items: McpServerRow[];
}

interface ToolListResponse {
  items: McpTool[];
}

interface CatalogToolsResponse {
  /** Set when the catalog was scoped to a virtual server (Round 1.14 / E5). */
  virtual_server?: { slug: string; name: string };
  available: McpTool[];
  /** Tools the user could call but the bundle excludes — shown only when vs is supplied. */
  requestable_for_bundle?: { tool: McpTool; reason: 'not_in_bundle' }[];
  /** Tools the user can't call yet because they're missing a WG. */
  requestable: { tool: McpTool; missing_wg?: string }[];
  hidden_count: number;
  /**
   * Full tool list of denied/closed tools. Populated only in admin
   * "view-as-user" mode (`?as_principal=` query param). Regular catalog
   * browsing keeps the count-only `hidden_count`.
   */
  hidden?: McpTool[];
}

export const QUERY_KEYS = {
  servers: ['servers'] as const,
  serversList: (filter: Record<string, string | undefined> = {}) => ['servers', filter] as const,
  server: (id: string) => ['servers', id] as const,
  toolsForServer: (serverId: string) => ['tools', 'server', serverId] as const,
  catalogTools: () => ['catalog', 'tools'] as const,
  catalogServers: () => ['catalog', 'servers'] as const,
};

export function useCatalogTools(opts?: { virtualServerSlug?: string; asPrincipal?: string }) {
  const params = new URLSearchParams();
  if (opts?.virtualServerSlug) params.set('vs', opts.virtualServerSlug);
  if (opts?.asPrincipal) params.set('as_principal', opts.asPrincipal);
  const query = params.toString();
  return useQuery({
    queryKey: [
      ...QUERY_KEYS.catalogTools(),
      ...(opts?.virtualServerSlug ? ['vs', opts.virtualServerSlug] : []),
      ...(opts?.asPrincipal ? ['as', opts.asPrincipal] : []),
    ],
    queryFn: () =>
      apiFetch<CatalogToolsResponse>(`/api/v1/catalog/tools${query ? `?${query}` : ''}`),
    enabled: opts?.asPrincipal === undefined || Boolean(opts.asPrincipal?.trim()),
  });
}

export function useCatalogServers() {
  return useQuery({
    queryKey: QUERY_KEYS.catalogServers(),
    queryFn: () => apiFetch<ServerListResponse>('/api/v1/catalog/servers'),
  });
}

export function useServers() {
  return useQuery({
    queryKey: QUERY_KEYS.servers,
    queryFn: () => apiFetch<ServerListResponse>('/api/v1/servers'),
  });
}

export function useServer(id: string | undefined) {
  return useQuery({
    queryKey: id ? QUERY_KEYS.server(id) : ['servers', 'noop'],
    queryFn: () => apiFetch<McpServerRow>(`/api/v1/servers/${id}`),
    enabled: Boolean(id),
  });
}

/**
 * Looks up a server by slug via the catalog list. There is no dedicated
 * `/api/v1/servers/by-slug/:slug` endpoint yet — the catalog list is small
 * enough that scanning it client-side is fine. Returns `undefined` data when
 * no server matches; the caller can then fall back to fixtures.
 */
export function useServerBySlug(slug: string | undefined) {
  const list = useCatalogServers();
  const match = list.data?.items.find((s) => s.slug === slug);
  return {
    data: match,
    isLoading: list.isLoading,
    isError: list.isError,
    isSuccess: list.isSuccess,
  };
}

export function useToolsForServer(serverId: string | undefined) {
  return useQuery({
    queryKey: serverId ? QUERY_KEYS.toolsForServer(serverId) : ['tools', 'noop'],
    queryFn: () => apiFetch<ToolListResponse>(`/api/v1/servers/${serverId}/tools`),
    enabled: Boolean(serverId),
  });
}
